import { Input, ReactIcons } from "@kalyzee/kalyzee-ui";
import { UserOutputDTO } from "@kalyzee/kast-web-sdk/http";
import { UserAvatar } from "@kalyzee/kast-web-sdk/web-components";
import clsx from "clsx";
import React, { useEffect, useMemo, useState } from "react";
import { useAppStoreSelector } from "../../contexts/appStore/selectors";
import { useSdkContext } from "../../contexts/sdk/context";
import {
  canImpersonate,
  getImpersonation,
  getImpersonationIdentities,
  getImpersonationUserLabel,
  getImpersonationUserName,
  isImpersonationTargetEmail,
  searchImpersonationTargets,
  startImpersonation,
  stopImpersonation,
} from "../../helpers/impersonation";
import { getImpersonationCollapsed, setImpersonationCollapsed } from "../../helpers/storage";
import { getUserAvatar, getUserDisplayName } from "../../helpers/user";
import styles from "./ImpersonationBar.module.css";

const SEARCH_DEBOUNCE_MS = 350;

/** Avatars are sized inline: UserAvatar's own class pins width/height to 100%. */
const AVATAR_STYLE = { width: "22px", height: "22px", flexShrink: 0 };

type PickMode = "search" | "ids";

/**
 * Debug bar letting an account holding `user:impersonate:any` run the app as
 * another user ("ghost mode"). The target is designated either by searching a
 * user, or by pasting its ids — same two values (user, identity) in both modes.
 *
 * Applying a target reloads the app: the websocket session, the store and the
 * meeting contexts were all built for the previous identity.
 */
export const ImpersonationBar = () => {
  const impersonation = getImpersonation();
  const sdkCtx = useSdkContext();
  const user = useAppStoreSelector((state) => state.user);

  const [collapsed, setCollapsed] = useState(() => getImpersonationCollapsed());
  const [hidden, setHidden] = useState(false);
  const [mode, setMode] = useState<PickMode>("search");

  // "ids" mode — raw values.
  const [userId, setUserId] = useState(impersonation?.target ?? "");
  const [identityId, setIdentityId] = useState(impersonation?.identityId ?? "");

  // "search" mode — query, hits, and the picked pair.
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<UserOutputDTO[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string>();
  const [picked, setPicked] = useState<UserOutputDTO>();
  const [pickedIdentityId, setPickedIdentityId] = useState<string>();

  useEffect(() => {
    if (mode !== "search") return;
    const cleaned = query.trim();
    if (!cleaned) {
      setResults([]);
      setSearchError(undefined);
      return;
    }
    let cancelled = false;
    setSearching(true);
    const timer = setTimeout(async () => {
      try {
        const found = await searchImpersonationTargets(sdkCtx.client, cleaned);
        if (cancelled) return;
        setResults(found);
        setSearchError(found.length ? undefined : "Aucun utilisateur trouvé");
      } catch (error) {
        if (cancelled) return;
        console.error("[ERROR] searchImpersonationTargets: ", error);
        setResults([]);
        setSearchError("Recherche impossible");
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query, mode, sdkCtx.client]);

  const identities = useMemo(
    () => (picked ? getImpersonationIdentities(picked) : []),
    [picked],
  );

  // Hidden for everyone but the actor — and kept visible while impersonating,
  // where the fetched permissions are the target's and no longer the actor's.
  if (!impersonation && !canImpersonate()) return null;
  if (hidden) return null;

  const trimmedUserId = userId.trim();
  const trimmedIdentityId = identityId.trim();
  // An email resolves the identity by itself, so the field would be dead weight.
  const showIdentityId = !!trimmedUserId && !isImpersonationTargetEmail(trimmedUserId);

  const nextTarget = mode === "search"
    ? (picked ? { target: picked.id, identityId: pickedIdentityId } : undefined)
    : (trimmedUserId
      ? { target: trimmedUserId, identityId: showIdentityId ? trimmedIdentityId || undefined : undefined }
      : undefined);

  const isCurrentTarget = !!nextTarget
    && impersonation?.target === nextTarget.target
    && impersonation?.identityId === nextTarget.identityId;

  const submit = () => {
    if (!nextTarget || isCurrentTarget) return;
    // The picker has done its job — come back collapsed after the reload.
    setImpersonationCollapsed(true);
    startImpersonation(nextTarget);
  };

  const toggleCollapsed = () => {
    const next = !collapsed;
    setImpersonationCollapsed(next);
    setCollapsed(next);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") submit();
  };

  /** Keeps a header control from also triggering the row's collapse toggle. */
  const withoutToggling = (action: () => void) => (e: React.MouseEvent) => {
    e.stopPropagation();
    action();
  };

  const pickUser = (candidate: UserOutputDTO) => {
    const candidateIdentities = getImpersonationIdentities(candidate);
    setPicked(candidate);
    // Preselect the first identity — the same one the API resolves for a bare
    // user id, so the default behaves identically while naming what it picked.
    // Only when there is a choice to make: with a single identity the picker
    // stays hidden and the bare form says it just as well.
    setPickedIdentityId(candidateIdentities.length > 1 ? candidateIdentities[0].id : undefined);
    setResults([]);
    setQuery("");
  };

  const renderSearchMode = () => (
    <div className={styles.modeContent}>
      {picked ? (
        <div className={styles.pickedRow}>
          <UserAvatar
            mode="image"
            image={getUserAvatar(picked)}
            username={getUserDisplayName(picked)}
            style={AVATAR_STYLE}
          />
          <span className={styles.picked}>{getImpersonationUserLabel(picked)}</span>
          <button type="button" className={styles.linkButton} onClick={() => setPicked(undefined)}>
            Changer
          </button>
        </div>
      ) : (
        <div className={styles.searchBlock}>
          <Input
            className={styles.input}
            nativeProps={{
              value: query,
              placeholder: "rechercher un utilisateur (nom, email)",
              onChange: (e) => setQuery(e.target.value),
            }}
          />
          {searching && <span className={styles.hint}>Recherche…</span>}
          {!searching && searchError && <span className={styles.hint}>{searchError}</span>}
          {!!results.length && (
            <ul className={styles.results}>
              {results.map((candidate) => (
                <li key={candidate.id}>
                  <button type="button" className={styles.result} onClick={() => pickUser(candidate)}>
                    <UserAvatar
                      mode="image"
                      image={getUserAvatar(candidate)}
                      username={getUserDisplayName(candidate)}
                      style={AVATAR_STYLE}
                    />
                    <span className={styles.resultLabel}>{getImpersonationUserLabel(candidate)}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {/* A user carries at most one identity per type. Listed in the server's
          own priority order, so the first chip — preselected — is the identity a
          bare user id would have resolved to. */}
      {!!picked && identities.length > 1 && (
        <div className={styles.identities}>
          <span className={styles.hint}>Identité :</span>
          {identities.map((identity) => (
            <button
              key={identity.id}
              type="button"
              className={clsx(styles.chip, pickedIdentityId === identity.id && styles.chipSelected)}
              onClick={() => setPickedIdentityId(identity.id)}
              title={identity.label}
            >
              {identity.type}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  const renderIdsMode = () => (
    <div className={styles.modeContent}>
      <Input
        className={styles.input}
        nativeProps={{
          value: userId,
          placeholder: "user id ou email",
          onChange: (e) => setUserId(e.target.value),
          onKeyDown,
        }}
      />
      {showIdentityId && (
        <Input
          className={styles.input}
          nativeProps={{
            value: identityId,
            placeholder: "identity id (optionnel)",
            onChange: (e) => setIdentityId(e.target.value),
            onKeyDown,
          }}
        />
      )}
    </div>
  );

  return (
    <div className={clsx(styles.bar, impersonation && styles.active)}>
      {/* The whole header row toggles — the chevron is only the visible
          affordance for it. The body is left out on purpose: it holds the
          picker, whose every click would otherwise fold the bar away.
          Each control inside stops propagation so it acts once, and only as
          itself. */}
      <div className={styles.header} onClick={toggleCollapsed}>
        <span className={styles.message}>
          {impersonation ? (
            <>
              {"⚠️ Vous êtes connecté en tant que "}
              <b>{user ? getImpersonationUserName(user) : impersonation.target}</b>
              {". Vos actions sont exécutées à travers cet utilisateur."}
            </>
          ) : (
            "Mode fantôme : exécuter l'application en tant qu'un autre utilisateur."
          )}
        </span>
        {impersonation && (
          <button type="button" className={styles.button} onClick={withoutToggling(stopImpersonation)}>
            Quitter
          </button>
        )}
        <button
          type="button"
          className={styles.collapseButton}
          onClick={withoutToggling(toggleCollapsed)}
          aria-expanded={!collapsed}
          title={collapsed ? "Déplier" : "Réduire"}
        >
          <ReactIcons.ArrowDown className={clsx(styles.chevron, !collapsed && styles.chevronOpen)} />
        </button>
        {/* Deliberately NOT persisted, unlike `collapsed`: hiding gets the bar
            out of the way for the current page, and a reload brings it back —
            so an active ghost session can never be forgotten behind a
            permanently dismissed banner. */}
        <button
          type="button"
          className={styles.collapseButton}
          onClick={withoutToggling(() => setHidden(true))}
          title="Masquer jusqu'au prochain rafraîchissement"
        >
          <ReactIcons.Close className={styles.chevron} />
        </button>
      </div>
      {!collapsed && (
        <div className={styles.body}>
          <div className={styles.modes}>
            <button
              type="button"
              className={clsx(styles.chip, mode === "search" && styles.chipSelected)}
              onClick={() => setMode("search")}
            >
              Recherche
            </button>
            <button
              type="button"
              className={clsx(styles.chip, mode === "ids" && styles.chipSelected)}
              onClick={() => setMode("ids")}
            >
              IDs
            </button>
          </div>
          {mode === "search" ? renderSearchMode() : renderIdsMode()}
          <button
            type="button"
            className={clsx(styles.button, styles.primary)}
            onClick={submit}
            disabled={!nextTarget || isCurrentTarget}
          >
            {impersonation ? "Changer" : "Devenir"}
          </button>
        </div>
      )}
    </div>
  );
};

export default ImpersonationBar;
